import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import confetti from "canvas-confetti";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sparkle, DownloadSimple, X, CircleNotch, ArrowRight, Crown,
  Envelope, Barbell, ForkKnife, Pill, ListChecks, BowlFood, Calendar,
  CheckSquare, Ruler, Camera, VideoCamera, Headset, Vault, Lightning, ChartLineUp, Package,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const ICONS = {
  Envelope, Barbell, ForkKnife, Pill, ListChecks, BowlFood, Calendar,
  CheckSquare, Ruler, Camera, Sparkle, Crown,
  VideoCamera, Headset, Vault, Lightning, ChartLineUp,
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function WelcomeModal({ open, onClose, autoConfetti = true }) {
  const { refreshUser } = useAuth();
  const { t } = useTranslation();
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [tab, setTab] = useState("pack");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get("/welcome-pack/manifest")
      .then((r) => setManifest(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || t("welcome_modal.pack_load_error")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Escape key closes modal (WCAG 2.1.2 No Keyboard Trap)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !autoConfetti || loading || !manifest) return;
    // Staggered confetti burst
    const end = Date.now() + 1400;
    const colors = ["#39FF14", "#ffffff", "#39FF14"];
    (function frame() {
      confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors });
      confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  }, [open, autoConfetti, loading, manifest]);

  const dismiss = async () => {
    try {
      await api.post("/welcome-pack/dismiss");
      await refreshUser();
    } catch (e) {
      console.error("[Welcome] dismiss:", e);
    }
    onClose?.();
  };

  const downloadZip = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${BACKEND}/api/welcome-pack/zip`, { credentials: "include" });
      if (!res.ok) throw new Error("Descarga falló");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `AS_TRAINING_Welcome_Pack_${(manifest?.plan || "premium").toUpperCase()}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("welcome_modal.pack_downloaded"), { description: t("welcome_modal.check_downloads_folder") });
      confetti({ particleCount: 80, spread: 70, origin: { y: 0.6 }, colors: ["#39FF14", "#ffffff"] });
    } catch (e) {
      toast.error(e.message || t("welcome_modal.download_failed"));
    } finally {
      setDownloading(false);
    }
  };

  const downloadOne = async (item) => {
    if (item.download?.type === "hub") {
      window.location.href = item.download.path;
      return;
    }
    if (item.download?.type !== "pdf") {
      toast.info(t("welcome_modal.item_coming"));
      return;
    }
    try {
      const res = await fetch(`${BACKEND}${item.download.endpoint}`, { credentials: "include" });
      if (!res.ok) throw new Error(t("welcome_modal.download_failed"));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${item.id}_AS_TRAINING.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("welcome_modal.item_downloaded"), { description: item.title });
    } catch (e) {
      toast.error(e.message || t("welcome_modal.item_download_error"));
    }
  };

  if (!open) return null;

  const isElite = manifest?.plan === "elite";
  const firstName = manifest?.user_name?.split(" ")[0] || t("welcome_modal.default_athlete_name");

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 overflow-y-auto"
      data-testid="welcome-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-title"
    >
      <div className="relative w-full max-w-3xl my-6 bg-as-black border-2 border-as-green shadow-[0_0_60px_rgba(57,255,20,0.25)]">
        {/* Close */}
        <button
          onClick={dismiss}
          className="absolute -top-3 -right-3 z-10 w-9 h-9 bg-as-green text-as-black rounded-full flex items-center justify-center hover:bg-white transition-colors"
          data-testid="welcome-close"
          aria-label={t("welcome_modal.close")}
        >
          <X size={18} weight="bold" />
        </button>

        {/* Header */}
        <div className="p-6 sm:p-10 border-b-2 border-as-green/40 bg-gradient-to-b from-as-green/10 to-transparent">
          <div className="flex items-center gap-2 mb-3">
            {isElite ? (
              <Crown size={20} weight="fill" color="#39FF14" />
            ) : (
              <Sparkle size={20} weight="fill" color="#39FF14" />
            )}
            <p className="as-label text-as-green">
              {isElite ? t("welcome_modal.welcome_elite") : t("welcome_modal.welcome_evolution")}
            </p>
          </div>
          <h2 className="as-heading text-3xl sm:text-5xl text-white leading-[1.05]" id="welcome-title" data-testid="welcome-title">
            {t("welcome_modal.greeting_prefix")} <span className="text-as-green">{firstName.toUpperCase()}.</span>
            <br />
            {t("welcome_modal.pack_ready")}
          </h2>
          <p className="text-as-gray mt-4 max-w-lg text-sm sm:text-base leading-relaxed">
            {t("welcome_modal.subtitle")}
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={downloadZip}
              disabled={downloading || loading}
              className="inline-flex items-center gap-2 bg-as-green text-as-black font-bold text-xs uppercase tracking-widest px-6 py-3 hover:bg-white transition-colors disabled:opacity-60"
              data-testid="welcome-download-zip"
            >
              {downloading ? (
                <CircleNotch size={16} weight="bold" className="animate-spin" />
              ) : (
                <DownloadSimple size={16} weight="bold" />
              )}
              {t("welcome_modal.download_zip")}
            </button>
            <button
              onClick={dismiss}
              className="inline-flex items-center gap-2 border border-as-border text-white font-bold text-xs uppercase tracking-widest px-6 py-3 hover:border-as-green hover:text-as-green transition-colors"
              data-testid="welcome-open-library"
            >
              {t("welcome_modal.explore_library")} <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        </div>

        {/* Tabs (ELITE has an extra tab) */}
        {isElite && (
          <div className="flex border-b border-as-border">
            <TabButton active={tab === "pack"} onClick={() => setTab("pack")} label={t("welcome_modal.tab_pack")} testid="welcome-tab-pack" />
            <TabButton active={tab === "elite"} onClick={() => setTab("elite")} label={t("welcome_modal.tab_elite")} testid="welcome-tab-elite" />
          </div>
        )}

        {/* Content */}
        <div className="p-6 sm:p-8 max-h-[55vh] overflow-y-auto">
          {loading ? (
            <div className="py-12 flex items-center justify-center">
              <CircleNotch size={28} className="animate-spin text-as-green" />
            </div>
          ) : tab === "elite" && isElite ? (
            <EliteBenefitsList benefits={manifest?.elite_benefits || []} />
          ) : (
            <PackItemsList items={manifest?.items || []} onDownload={downloadOne} />
          )}
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, label, testid }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 px-4 py-3 text-xs font-bold uppercase tracking-widest transition-colors ${
        active
          ? "text-as-green border-b-2 border-as-green bg-as-green/5"
          : "text-as-gray hover:text-white"
      }`}
      data-testid={testid}
    >
      {label}
    </button>
  );
}

function PackItemsList({ items, onDownload }) {
  const { t } = useTranslation();
  // Group by folder
  const groups = items.reduce((acc, it) => {
    (acc[it.folder] = acc[it.folder] || []).push(it);
    return acc;
  }, {});
  return (
    <div className="space-y-6">
      {Object.entries(groups).map(([folder, list]) => (
        <div key={folder}>
          <p className="as-label text-as-green mb-3">{folder.replace(/^\d+-/, "").replace(/-/g, " ")}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {list.map((it) => {
              const Icon = ICONS[it.icon] || Package;
              const isComing = it.status === "coming";
              const isHub = it.download?.type === "hub";
              return (
                <button
                  key={it.id}
                  onClick={() => !isComing && onDownload(it)}
                  disabled={isComing}
                  className={`text-left border p-3 flex items-start gap-3 transition-colors ${
                    isComing
                      ? "border-as-border opacity-50 cursor-not-allowed"
                      : "border-as-border hover:border-as-green"
                  }`}
                  data-testid={`welcome-item-${it.id}`}
                >
                  <Icon size={22} weight="duotone" color="#39FF14" className="flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm font-bold leading-tight">{it.title}</p>
                    <p className="text-[11px] text-as-gray mt-1 leading-snug">{it.desc}</p>
                    {isComing ? (
                      <p className="text-[9px] text-yellow-400 mt-2 uppercase tracking-wider font-bold">{t("welcome_modal.item_coming")}</p>
                    ) : isHub ? (
                      <p className="text-[9px] text-as-green mt-2 uppercase tracking-wider font-bold">{t("welcome_modal.item_open")}</p>
                    ) : (
                      <p className="text-[9px] text-as-green mt-2 uppercase tracking-wider font-bold flex items-center gap-1">
                        <DownloadSimple size={10} weight="bold" /> {t("welcome_modal.item_download")}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function EliteBenefitsList({ benefits }) {
  const { t } = useTranslation();
  return (
    <div>
      <p className="as-label text-as-green mb-3">{t("welcome_modal.elite_exclusive")}</p>
      <p className="text-as-gray text-sm mb-6 leading-relaxed">
        {t("welcome_modal.elite_intro")}
      </p>
      <div className="space-y-3">
        {benefits.map((b, i) => {
          const Icon = ICONS[b.icon] || Sparkle;
          return (
            <div
              key={b.id}
              className="flex items-start gap-4 p-4 border border-as-border bg-as-surface hover:border-as-green transition-colors"
              data-testid={`elite-benefit-${b.id}`}
            >
              <div className="w-10 h-10 rounded-full bg-as-green/10 border border-as-green/40 flex items-center justify-center flex-shrink-0">
                <Icon size={18} weight="duotone" color="#39FF14" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold text-as-green">0{i + 1}</span>
                  <h4 className="text-white font-bold text-sm">{b.title}</h4>
                </div>
                <p className="text-xs text-as-gray leading-relaxed">{b.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
